import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  Archive,
  ArchiveRestore,
  Folder,
  FolderCog,
  Lock,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react'
import { type FormEvent, Fragment, useEffect, useState } from 'react'
import {
  BulkActionBar,
  EmptyTable,
  GroupHeaderRow,
  Pager,
  RowCheckbox,
  SelectAllCheckbox,
  SortableHeader,
  TableToolbar,
} from '../components/data-table'
import { EntityAccessPanel } from '../components/entity-access-panel'
import { ManualGroupPanel } from '../components/manual-group-panel'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus, BarSlot } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { SidePanel } from '../components/ui/side-panel'
import { TableShell } from '../components/ui/table-shell'
import { api, type Service } from '../lib/api'
import { useDataTable } from '../lib/data-table'
import { formatCurrency } from '../lib/format'
import { type GroupOption, useGroupedTable } from '../lib/grouped-table'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

const SERVICE_GROUP_OPTIONS: GroupOption[] = [
  { key: 'none', label: 'Keine' },
  { key: 'status', label: 'Aktiv / Archiviert' },
]

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/services',
  component: ServicesPage,
})

export function ServicesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [dialogState, setDialogState] = useState<{ open: boolean; service: Service | null }>({
    open: false,
    service: null,
  })
  // Mobile-Shell (<640): Suche-Sheet hinterm Bottom-Slot; Titel nur
  // standalone — eingebettet (/stammdaten-Tab) gehört er der Host-Seite.
  const [searchSheet, setSearchSheet] = useState(false)
  useMobilePageTitle(embedded ? null : 'Tätigkeiten')
  const [accessPanel, setAccessPanel] = useState<{
    open: boolean
    service: Service | null
    bulkIds: string[] | null
  }>({ open: false, service: null, bulkIds: null })
  const [groupPanel, setGroupPanel] = useState<
    { open: boolean; mode: 'assign'; ids: string[] } | { open: boolean; mode: 'manage' }
  >({ open: false, mode: 'manage' })
  const [showArchived, setShowArchived] = useState(false)

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data, isLoading } = useQuery({
    queryKey: ['services', { archived: showArchived }],
    queryFn: () => api.listServices(showArchived),
    enabled: !!meData?.user,
  })
  const { data: groupsData } = useQuery({
    queryKey: ['groups', 'service'],
    queryFn: () => api.listEntityGroups('service'),
    enabled: !!meData?.user,
  })

  const services = data?.services ?? []
  const serviceGroups = groupsData?.groups ?? []
  const isAdminUser = meData?.user?.role === 'admin'

  const table = useDataTable<Service>(
    services,
    {
      name: 'name',
      rate: 'defaultHourlyRateCents',
      billable: 'billable',
    },
    {
      searchableFields: ['name'],
      defaultSortKey: 'name',
      defaultSortDir: 'asc',
      defaultPageSize: 25,
      storageKey: 'services',
    },
  )

  const grouped = useGroupedTable<Service>(table, {
    pageKey: 'services',
    options: SERVICE_GROUP_OPTIONS,
    manualGroups: serviceGroups,
    manualGroupsEnabled: isAdminUser,
    getId: (s) => s.id,
    groupBy: (s, mode) => {
      if (mode === 'status') {
        return s.archived
          ? { key: 'archived', label: 'Archiviert' }
          : { key: 'active', label: 'Aktiv' }
      }
      return null
    },
  })

  const queryClient = useQueryClient()
  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteServices(ids),
    onSuccess: (r) => {
      table.clearSelection()
      queryClient.invalidateQueries({ queryKey: ['services'] })
      notify.success(`${r.deleted} Tätigkeiten gelöscht`)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  const isAdmin = meData.user.role === 'admin'

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Suche → Sheet + ＋ Neue Tätigkeit —
          Handler identisch zu den Kopf-/Toolbar-Controls darunter. */}
      <MobileBottomSlots>
        <BarSlot
          label="Suche"
          icon={<Search className="h-5 w-5" />}
          active={table.search !== ''}
          ariaLabel="Tätigkeiten durchsuchen"
          ariaHasPopup
          onClick={() => setSearchSheet(true)}
        />
        <BarAccentPlus
          ariaLabel="Neue Tätigkeit"
          onClick={() => setDialogState({ open: true, service: null })}
        />
      </MobileBottomSlots>

      {/* Suche-Sheet (<640): bindet dieselbe table.search wie die
          Toolbar-Suche — Treffer filtern live hinter dem Sheet. */}
      <SidePanel open={searchSheet} onClose={() => setSearchSheet(false)} title="Suche">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
          <Input
            value={table.search}
            onChange={(e) => table.setSearch(e.target.value)}
            placeholder="Tätigkeiten durchsuchen …"
            className="pl-8"
            autoFocus
          />
        </div>
      </SidePanel>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Tätigkeiten"
        actions={
          <Button size="sm" onClick={() => setDialogState({ open: true, service: null })}>
            <Plus className="h-3.5 w-3.5" /> Neue Tätigkeit
          </Button>
        }
      />

      <TableToolbar
        search={table.search}
        onSearch={table.setSearch}
        filteredRows={table.filteredRows}
        totalRows={table.totalRows}
        pageSize={table.pageSize}
        onPageSize={table.setPageSize}
        groupBy={{
          value: grouped.groupBy,
          options: grouped.options,
          onChange: grouped.setGroupBy,
          onExpandAll: grouped.expandAll,
          onCollapseAll: grouped.collapseAll,
          allCollapsed: grouped.allCollapsed,
        }}
        rightSlot={
          <>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setGroupPanel({ open: true, mode: 'manage' })}
                title="Gruppen verwalten"
                className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-text-tertiary hover:text-accent hover:bg-bg-overlay/40 transition-colors"
              >
                <FolderCog className="h-3.5 w-3.5" />
                <span className="text-xs whitespace-nowrap">Gruppen verwalten</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className="text-xs text-text-tertiary hover:text-accent transition-colors whitespace-nowrap"
            >
              {showArchived ? 'Aktive zeigen' : 'Archivierte zeigen'}
            </button>
          </>
        }
      />

      <TableShell className="rounded-2xl bg-bg-elevated/40">
        {isLoading ? (
          <div className="px-6 py-12 text-center text-text-secondary">Lade …</div>
        ) : services.length === 0 ? (
          <EmptyTable message="Noch keine Tätigkeiten. Lege z.B. „Programmierung“ mit 95 €/h an." />
        ) : table.rows.length === 0 ? (
          <EmptyTable message="Keine Treffer." hint="Suche oder Filter anpassen." />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-bg-overlay/40">
              <tr className="text-left">
                {isAdmin && (
                  <th className="pl-5 pr-2 py-2.5 w-10">
                    <SelectAllCheckbox
                      checked={table.allOnPageSelected}
                      indeterminate={table.someOnPageSelected}
                      onToggle={table.toggleAllOnPage}
                    />
                  </th>
                )}
                <th className="px-5 py-2.5">
                  <SortableHeader
                    label="Name"
                    sortKey="name"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-5 py-2.5">
                  <SortableHeader
                    label="Abrechenbar"
                    sortKey="billable"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-5 py-2.5">
                  <SortableHeader
                    label="Default-Satz"
                    sortKey="rate"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                    align="right"
                  />
                </th>
                <th className="px-5 py-2.5 w-px" aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {grouped.disabled
                ? table.rows.map((s) => (
                    <ServiceRow
                      key={s.id}
                      service={s}
                      isAdmin={isAdmin}
                      selected={table.isSelected(s.id)}
                      onSelect={() => table.toggleRow(s.id)}
                      onEdit={() => setDialogState({ open: true, service: s })}
                      onManageAccess={() =>
                        setAccessPanel({ open: true, service: s, bulkIds: null })
                      }
                    />
                  ))
                : grouped.groups.map((g) => {
                    const ids = g.items.map((s) => s.id)
                    const selectedInGroup = ids.filter((id) => table.isSelected(id)).length
                    const groupChecked = selectedInGroup > 0 && selectedInGroup === ids.length
                    const groupIndeterminate = selectedInGroup > 0 && selectedInGroup < ids.length
                    return (
                      <Fragment key={g.key}>
                        <GroupHeaderRow
                          label={g.label}
                          count={g.items.length}
                          restrictedCount={g.restrictedCount}
                          collapsed={g.collapsed}
                          onToggle={() => grouped.toggleGroup(g.key)}
                          colSpan={isAdmin ? 5 : 4}
                          groupChecked={isAdmin ? groupChecked : undefined}
                          groupIndeterminate={isAdmin ? groupIndeterminate : undefined}
                          color={g.color}
                          muted={g.muted}
                          onToggleAll={
                            isAdmin
                              ? () => {
                                  const shouldClear =
                                    selectedInGroup === ids.length && ids.length > 0
                                  for (const id of ids) {
                                    const isOn = table.isSelected(id)
                                    if (shouldClear && isOn) table.toggleRow(id)
                                    else if (!shouldClear && !isOn) table.toggleRow(id)
                                  }
                                }
                              : undefined
                          }
                        />
                        {!g.collapsed &&
                          g.items.map((s) => (
                            <ServiceRow
                              key={s.id}
                              service={s}
                              isAdmin={isAdmin}
                              selected={table.isSelected(s.id)}
                              onSelect={() => table.toggleRow(s.id)}
                              onEdit={() => setDialogState({ open: true, service: s })}
                              onManageAccess={() =>
                                setAccessPanel({ open: true, service: s, bulkIds: null })
                              }
                            />
                          ))}
                      </Fragment>
                    )
                  })}
            </tbody>
          </table>
        )}
      </TableShell>

      <Pager
        page={table.page}
        totalPages={table.totalPages}
        onPage={table.setPage}
        filteredRows={table.filteredRows}
        pageSize={table.pageSize}
      />

      {isAdmin && (
        <BulkActionBar
          selectionCount={table.selectionCount}
          filteredRows={table.filteredRows}
          onClear={table.clearSelection}
          onSelectAllFiltered={table.selectAllFiltered}
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setGroupPanel({
                open: true,
                mode: 'assign',
                ids: Array.from(table.selectedIds),
              })
            }
          >
            <Folder className="h-3.5 w-3.5" /> Zur Gruppe
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setAccessPanel({
                open: true,
                service: null,
                bulkIds: Array.from(table.selectedIds),
              })
            }
          >
            <Lock className="h-3.5 w-3.5" /> Freigabe
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={bulkDelete.isPending}
            onClick={async () => {
              const ids = Array.from(table.selectedIds)
              const ok = await confirm({
                title: `${ids.length} Tätigkeiten löschen?`,
                description: 'Diese Aktion ist unumkehrbar.',
                variant: 'danger',
                confirmLabel: 'Endgültig löschen',
              })
              if (ok) bulkDelete.mutate(ids)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Löschen
          </Button>
        </BulkActionBar>
      )}

      <ServiceFormDialog
        open={dialogState.open}
        service={dialogState.service}
        onClose={() => setDialogState({ open: false, service: null })}
      />
      <EntityAccessPanel
        open={accessPanel.open}
        onClose={() => setAccessPanel({ open: false, service: null, bulkIds: null })}
        resourceType="service"
        resourceId={accessPanel.service?.id ?? null}
        resourceIds={accessPanel.bulkIds ?? undefined}
        resourceName={
          accessPanel.bulkIds
            ? `${accessPanel.bulkIds.length} Tätigkeiten`
            : (accessPanel.service?.name ?? '')
        }
      />
      {isAdmin &&
        (groupPanel.mode === 'assign' ? (
          <ManualGroupPanel
            open={groupPanel.open}
            onClose={() => setGroupPanel({ open: false, mode: 'manage' })}
            resourceType="service"
            mode="assign"
            resourceIds={groupPanel.ids}
          />
        ) : (
          <ManualGroupPanel
            open={groupPanel.open}
            onClose={() => setGroupPanel({ open: false, mode: 'manage' })}
            resourceType="service"
            mode="manage"
          />
        ))}
    </div>
  )
}

function ServiceRow({
  service,
  isAdmin,
  selected,
  onSelect,
  onEdit,
  onManageAccess,
}: {
  service: Service
  isAdmin: boolean
  selected: boolean
  onSelect: () => void
  onEdit: () => void
  onManageAccess: () => void
}) {
  const queryClient = useQueryClient()
  const archive = useMutation({
    mutationFn: () => api.updateService(service.id, { archived: !service.archived }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] })
      notify.success(service.archived ? 'Tätigkeit reaktiviert' : 'Tätigkeit archiviert')
    },
    onError: (err: Error) => notify.error(err.message),
  })
  const remove = useMutation({
    mutationFn: () => api.deleteService(service.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] })
      notify.success('Tätigkeit gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const rate =
    service.defaultHourlyRateCents != null
      ? `${formatCurrency(service.defaultHourlyRateCents)}/h`
      : '—'

  return (
    <tr
      className={`group transition-colors hover:bg-bg-overlay/40 ${
        service.archived ? 'opacity-60' : ''
      } ${selected ? 'bg-accent/5' : ''}`}
    >
      {isAdmin && (
        <td className="pl-5 pr-2 py-3">
          <RowCheckbox checked={selected} onToggle={onSelect} label={`${service.name} auswählen`} />
        </td>
      )}
      <td className="px-5 py-3 text-sm font-medium text-text-primary truncate">
        <span className="flex items-center gap-1.5 truncate">
          {service.restricted && (
            <span title="Beschränkter Zugriff" className="shrink-0">
              <Lock className="h-3 w-3 text-accent" aria-label="Beschränkter Zugriff" />
            </span>
          )}
          <span className="truncate">{service.name}</span>
        </span>
      </td>
      <td className="px-5 py-3">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] ${
            service.billable ? 'bg-accent/15 text-accent' : 'bg-bg-overlay text-text-tertiary'
          }`}
        >
          {service.billable ? 'abrechenbar' : 'nicht abrechenbar'}
        </span>
      </td>
      <td className="px-5 py-3 text-right font-mono text-sm text-accent tabular-nums whitespace-nowrap">
        {rate}
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onEdit}
            aria-label="Bearbeiten"
            className="text-text-tertiary hover:text-accent"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onManageAccess}
              aria-label="Freigabe verwalten"
              title="Freigabe verwalten"
              className={
                service.restricted
                  ? 'text-accent hover:text-accent'
                  : 'text-text-tertiary hover:text-accent'
              }
            >
              <Lock className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => archive.mutate()}
            aria-label={service.archived ? 'Reaktivieren' : 'Archivieren'}
            className="text-text-tertiary hover:text-accent"
          >
            {service.archived ? (
              <ArchiveRestore className="h-4 w-4" />
            ) : (
              <Archive className="h-4 w-4" />
            )}
          </Button>
          {isAdmin && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={async () => {
                const ok = await confirm({
                  title: 'Tätigkeit löschen',
                  description: `„${service.name}“ wirklich löschen?`,
                  variant: 'danger',
                  confirmLabel: 'Löschen',
                })
                if (ok) remove.mutate()
              }}
              aria-label="Löschen"
              className="text-text-tertiary hover:text-danger"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

function ServiceFormDialog({
  open,
  service,
  onClose,
}: {
  open: boolean
  service: Service | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const isEdit = !!service
  const [name, setName] = useState('')
  const [rate, setRate] = useState('')
  const [billable, setBillable] = useState(true)

  useEffect(() => {
    if (!open) return
    if (service) {
      setName(service.name)
      setRate(
        service.defaultHourlyRateCents != null ? String(service.defaultHourlyRateCents / 100) : '',
      )
      setBillable(service.billable)
    } else {
      setName('')
      setRate('')
      setBillable(true)
    }
  }, [service, open])

  const save = useMutation({
    mutationFn: () => {
      const cents = rate ? Math.round(Number(rate) * 100) : null
      if (isEdit && service) {
        return api.updateService(service.id, { name, defaultHourlyRateCents: cents, billable })
      }
      return api.createService({ name, defaultHourlyRateCents: cents, billable })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] })
      notify.success(isEdit ? 'Tätigkeit aktualisiert' : 'Tätigkeit angelegt')
      setName('')
      setRate('')
      setBillable(true)
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    save.mutate()
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={isEdit ? 'Tätigkeit bearbeiten' : 'Neue Tätigkeit'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={!name.trim() || save.isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="svc-name">Name</Label>
          <Input
            id="svc-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Programmierung"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="svc-rate">Default-Stundensatz (€) — optional</Label>
          <Input
            id="svc-rate"
            type="number"
            min="0"
            step="0.5"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="95"
          />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={billable}
            onChange={(e) => setBillable(e.target.checked)}
            className="h-4 w-4 accent-accent"
          />
          <span>Abrechenbar</span>
        </label>
      </form>
    </SidePanel>
  )
}
